import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ILSType, RunwayMarkingsCondition, TakeoffLandArea, TrafficPatternDirection } from '../models/takeoff-land-area.model';
import { MAT_DIALOG_DATA, MatDialogRef, MatSnackBar } from '@angular/material';
import { RunwayService } from '../service/runway.service';
import { LandingSurface } from '../models/landing-surface.model';
import { LatLon } from '../../util/geodesy/latlon';

@Component({
  selector: 'app-edit-takeoff-land-area',
  templateUrl: './edit-takeoff-land-area.component.html',
  styleUrls: ['./edit-takeoff-land-area.component.scss']
})
export class EditTakeoffLandAreaComponent implements OnInit {
  takeoffLandArea: TakeoffLandArea;
  origtakeoffLandArea: TakeoffLandArea;
  isNew = false;
  isLoading = true;
  runwayId: number;
  airportId: number;
  formGroup: FormGroup;
  selectedTrafficPatternDirection: string;
  selectedMarkingsCondition: string;
  selectedIlsType: string;
  latitude: number;
  longitude: number;

  constructor(private _matDialogRef: MatDialogRef<EditTakeoffLandAreaComponent>,
              public _snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data,
              private _runwayService: RunwayService) {
    this.takeoffLandArea = data.takeoffLandArea || new TakeoffLandArea();
    this.isNew = data.isNew || false;
    this.runwayId = data.runwayId;
    this.airportId = data.airportId;
    this.origtakeoffLandArea = Object.assign(new TakeoffLandArea, data.takeoffLandArea);

    if (this.takeoffLandArea.location != null) {
      this.latitude = this.takeoffLandArea.location.lat;
      this.longitude = this.takeoffLandArea.location.lon;
    } else {
      this.latitude = null;
      this.longitude = null;
    }

    if (this.isNew) {
      this.selectedTrafficPatternDirection = '';
      this.selectedMarkingsCondition = '';
      this.selectedIlsType = '';
    } else {
      this.selectedTrafficPatternDirection = this.getTrafficPatternDirection(this.takeoffLandArea.trafficPatDirFixed);
      this.selectedMarkingsCondition = this.getMarkingsCondition(this.takeoffLandArea.markingsCondition);
      this.selectedIlsType = this.getIlsType(this.takeoffLandArea.ilsType);
    }
  }

  ngOnInit() {
    this._matDialogRef.backdropClick().subscribe(() => {
      this.close({cancelBtnClicked: true});
    });
    this.formGroup = new FormGroup({
      name: new FormControl(this.takeoffLandArea.name, [Validators.required, Validators.maxLength(10)]),
      latitude: new FormControl(this.latitude, [Validators.required, Validators.pattern('^-?\\d{0,3}\\.?\\d{0,7}$')]),
      longitude: new FormControl(this.longitude, [Validators.required, Validators.pattern('^-?\\d{0,3}\\.?\\d{0,7}$')]),
      trafficPatternDirection: new FormControl(this.selectedTrafficPatternDirection),
      elevation: new FormControl(this.takeoffLandArea.elevation, [Validators.pattern('^\\d{0,10}\\.?\\d{0,1}$')]),
      trueAlignment: new FormControl(this.takeoffLandArea.trueAlignment, [Validators.required, Validators.pattern('^\\d{0,3}$')]),
      markingsType: new FormControl(this.takeoffLandArea.markingsType, [Validators.maxLength(30)]),
      markingsCondition: new FormControl(this.selectedMarkingsCondition),
      ilsType: new FormControl(this.selectedIlsType),
      gradient: new FormControl(this.takeoffLandArea.gradient, [Validators.pattern('^\\d{0,4}\\.?\\d{0,2}$')])
    });
    this.isLoading = false;
  }

  getTrafficPatternDirection(dir: TrafficPatternDirection): string {
    return TakeoffLandArea.formatTrafficPatternDirection(dir);
  }

  setTrafficPatternDirection(dir: string): TrafficPatternDirection {
    return TakeoffLandArea.parseTrafficPatternDirection(dir);
  }

  getMarkingsCondition(condition: RunwayMarkingsCondition): string {
    return TakeoffLandArea.formatRunwayMarkingsCondition(condition);
  }

  setMarkingsCondition(condition: string): RunwayMarkingsCondition {
    return  TakeoffLandArea.parseRunwayMarkingsCondition(condition);
  }

  getIlsType(type: ILSType): string {
    return TakeoffLandArea.formatILSType(type);
  }

  setIlsType(type: string): ILSType {
    return TakeoffLandArea.parseILSType(type);
  }

  /**
   * triggered by save button click event
   * @return {Promise<void>}
   */
  async save() {
    try {
      let result: any;
      this.takeoffLandArea.name = this.formGroup.value.name;
      this.takeoffLandArea.location = new LatLon(this.formGroup.value.latitude, this.formGroup.value.longitude);
      this.takeoffLandArea.trafficPatDirFixed = this.setTrafficPatternDirection(this.formGroup.value.trafficPatternDirection);
      this.takeoffLandArea.elevation = this.formGroup.value.elevation;
      this.takeoffLandArea.trueAlignment = this.formGroup.value.trueAlignment;
      this.takeoffLandArea.markingsType = this.formGroup.value.markingsType;
      this.takeoffLandArea.markingsCondition = this.setMarkingsCondition(this.formGroup.value.markingsCondition);
      this.takeoffLandArea.ilsType = this.setIlsType(this.formGroup.value.ilsType);
      this.takeoffLandArea.gradient = this.formGroup.value.gradient;

      if (this.isNew) {
        result = await this._runwayService.postTakeoffLandArea(this.airportId, this.takeoffLandArea, this.runwayId);
        this.takeoffLandArea = TakeoffLandArea.parse(result);
      } else {
        result = await this._runwayService.putTakeoffLandArea(this.airportId, this.takeoffLandArea);
        this.takeoffLandArea = TakeoffLandArea.parse(result);
      }
      this.close({cancelBtnClicked: false, success: true, takeoffLandArea: this.takeoffLandArea});
    } catch (e) {
      this.close({cancelBtnClicked: false, success: false, error: e});
    }
  }


  /**
   * triggered by cancel button click event
   */
  cancel () {
    this.close({cancelBtnClicked: true});
  }

  /**
   * closes modal
   * @param data
   */
  close(data: any) {
    if (data && data.cancelBtnClicked) {
      Object.assign(this.takeoffLandArea, this.origtakeoffLandArea);
    }
    data['isNew'] = this.isNew;
    this._matDialogRef.close(data);
  }
}
